import { renderDateTimeView, renderDateView, renderMoneyView } from '@/Ship/Helpers/fieldViews'

import { APPLICATION_PAYMENT_REST_URL } from '../Contracts/IApplication'
import { ApplicationPaymentFormTemplate } from '../Templates/ApplicationPaymentFormTemplate'

import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

/** Лише перегляд: рахує або ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Найдовший коментар (`ApplicationPaymentEntity::COMMENT_MAX_LENGTH`). */
const COMMENT_MAX_LENGTH = 4096

/**
 * Поля, яких бек від форми не приймає: суму в базовій валюті рахує він сам,
 * службові — аудит; заявку задають лише при створенні.
 */
const READ_ONLY_KEYS = ['id', 'externalCode', 'sumBaseCurrency', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'] as const

const preparePaymentPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['application']
}

/**
 * Оплати заявки — вкладка «Оплати» її картки (`SPEC-021` §8.7, Q2.7).
 *
 * Власного реєстру немає (без `pageUrl`); створюють оплату лише з картки
 * заявки (`CREATED_FROM_PARENT`). Права — `ApplicationPayment` беку: «Фінанси»
 * й логісти, будь-яка видима заявка (Q2.7). Ролей ресурс не питає.
 */
export const ApplicationPaymentResource: IResourceConfig = {
    title: 'Оплати заявок',
    fields: {
        date: {
            label: 'Дата оплати',
            type: 'date',
            isRequired: true,
            view: renderDateView,
        },
        sum: {
            label: 'Сума оплати',
            type: 'number',
            isRequired: true,
            inputProps: { min: 0, precision: 2, style: { width: '100%' } },
        },
        sumCurrency: {
            label: 'Валюта',
            type: 'select',
            resource: 'Setting.ListCurrency',
            responseLabel: (item: { code?: unknown; title?: unknown }) =>
                typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
        },
        sumBaseCurrency: readOnly({
            label: 'Сума транзакції в базовій валюті',
            type: 'number',
            view: renderMoneyView,
        }),
        comment: {
            label: 'Коментар',
            type: 'textarea',
            inputProps: { maxLength: COMMENT_MAX_LENGTH },
        },
        createdAt: readOnly({
            label: 'Створено',
            type: 'datetime',
            view: renderDateTimeView,
        }),
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                ApplicationPaymentPayload: preparePaymentPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: ApplicationPaymentFormTemplate },
            edit: { template: ApplicationPaymentFormTemplate },
            create: { template: ApplicationPaymentFormTemplate },
        },
    },
    // Як у легасі (`ApplicationPaymentCrud.jsx`)
    drawer: {
        width: 600,
    },
    restUrl: APPLICATION_PAYMENT_REST_URL,
}
